import React, { Dispatch } from "react";
import { useIntl } from "react-intl";
import { DirectionalHint, IconButton, Stack, TooltipHost } from "@fluentui/react";
import { ChoiceGroup, IChoiceGroupOption } from "@fluentui/react/lib/ChoiceGroup";
import { wizardStyles } from "cayo.ui.wizards";
import styled from "styled-components";
import { messages } from "../messages";
import { AzureSubscriptionFlowType } from "../types";

interface ISelectFlow {
  dispatchWizardState: Dispatch<{ type: string; payload: any }>;
  wizardState: any;
}

export const SelectFlow = ({ wizardState, dispatchWizardState }: ISelectFlow) => {
  const intl = useIntl();

  const makeOption = (key: string, text: string, tooltip: string): IChoiceGroupOption => ({
    key,
    text,
    ariaLabel: text,
    onRenderField: (props, render) => (
      <OptionWrap>
        {render!(props)}
        <TooltipHost content={tooltip} directionalHint={DirectionalHint.topCenter}>
          <IconButton
            iconProps={{ iconName: "Info" }}
            ariaLabel={tooltip}
            styles={{ root: { height: 16, width: 16 }, icon: { fontSize: 12 } }}
          />
        </TooltipHost>
      </OptionWrap>
    ),
  });

  const options: IChoiceGroupOption[] = [
    makeOption(
      "default",
      intl.formatMessage(messages.flowOptionDefault),
      intl.formatMessage(messages.flowOptionDefaultDescription)
    ),
    makeOption(
      "applicationCertificate",
      intl.formatMessage(messages.flowOptionAppCert),
      intl.formatMessage(messages.flowOptionAppCertDescription)
    ),
  ];

  const handleChange = (_: React.FormEvent | undefined, option: IChoiceGroupOption | undefined) => {
    if (!option) {
      return;
    }

    dispatchWizardState({
      type: "update",
      payload: {
        selectedFlow: option.key as AzureSubscriptionFlowType,
        // Reset shared subscription state to prevent stale data from the other flow
        subscription: { subscriptionId: "", name: "" },
        subscriptionsError: "",
        subscriptionLoadTrigger: 0,
      },
    });
  };

  return (
    <Stack
      horizontal={false}
      tokens={wizardStyles.stackTokens}
      styles={{ root: { maxWidth: wizardStyles.maxPageWidth } }}
    >
      <Title>
        <p className="title-text">{intl.formatMessage(messages.selectFlowLabel)}</p>
        <span>*</span>
      </Title>
      <ChoiceGroup
        selectedKey={wizardState.selectedFlow || "default"}
        options={options}
        onChange={handleChange}
      />
    </Stack>
  );
};

const OptionWrap = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  .title-text {
    margin: 0;
    font-weight: 600 !important;
  }

  span {
    color: ${(props) => props.theme.cayoTheme.brandColors.error};
  }
`;
